'use strict';

const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');

const internals = {};

internals.createEmitter = () => {

    const listenersByEvent = new Map();

    return {
        emit: (event, payload) => {

            const listeners = listenersByEvent.get(event) || [];

            for (const listener of listeners) {
                listener(payload);
            }
        },
        on: (event, listener) => {

            const listeners = listenersByEvent.get(event) || [];
            listeners.push(listener);
            listenersByEvent.set(event, listeners);
        },
        removeListener: (event, listener) => {

            const listeners = listenersByEvent.get(event) || [];
            listenersByEvent.set(event, listeners.filter((candidate) => candidate !== listener));
        },
        removeAllListeners: (event) => {

            if (event) {
                listenersByEvent.delete(event);
                return;
            }

            listenersByEvent.clear();
        }
    };
};

internals.toApiBaseUrl = (context) => {

    const apiPrefix = context.configuration?.api?.prefix || '/api';
    const configuredHost = context.configuration?.api?.host || '';

    if (configuredHost) {
        return `${configuredHost}${apiPrefix}`;
    }

    return apiPrefix;
};

internals.createHttp = (context) => {

    const apiBaseUrl = internals.toApiBaseUrl(context).replace(/\/$/, '');

    const request = async (path, options = {}) => {

        const response = await fetch(`${apiBaseUrl}${path}`, {
            ...options,
            headers: {
                'content-type': 'application/json',
                ...(options.headers || {})
            }
        });

        if (response.status === 204) {
            return null;
        }

        const payload = await response.json();

        if (!response.ok) {
            const error = new Error(payload.error || response.statusText || 'E2E Twilio request failed');
            error.status = response.status;
            error.code = payload.code;
            throw error;
        }

        return payload;
    };

    return {
        delete: (path) => request(path, { method: 'DELETE' }),
        get: (path) => request(path),
        patch: (path, payload) => request(path, { method: 'PATCH', body: JSON.stringify(payload) }),
        post: (path, payload) => request(path, { method: 'POST', body: JSON.stringify(payload) })
    };
};

internals.createPaginator = (items) => ({
    hasNextPage: false,
    hasPrevPage: false,
    items,
    nextPage: async () => internals.createPaginator([]),
    prevPage: async () => internals.createPaginator([])
});

internals.createMessage = ({ http, emitter, raw }) => {

    const message = {
        ...raw,
        attributes: raw.attributes || {},
        delivery: raw.delivery || {},
        state: {
            sid: raw.sid
        },
        updateBody: async (body) => {

            const { message: updatedRaw } = await http.patch(
                `/__e2e__/twilio/conversations/${raw.conversation.sid}/messages/${raw.sid}`,
                { body, attributes: message.attributes }
            );
            const updated = internals.createMessage({ http, emitter, raw: updatedRaw });
            emitter.emit('messageUpdated', { message: updated });

            return updated;
        },
        remove: async () => {

            await http.delete(`/__e2e__/twilio/conversations/${raw.conversation.sid}/messages/${raw.sid}`);
            emitter.emit('messageRemoved', message);

            return message;
        }
    };

    return message;
};

internals.createConversation = ({ http, emitter, raw }) => {

    const conversation = {
        ...raw,
        getMessages: async (pageSize = 100) => {

            const { messages } = await http.get(`/__e2e__/twilio/conversations/${raw.sid}/messages?order=desc&limit=${pageSize}`);

            return internals.createPaginator(messages.map((message) => internals.createMessage({
                http,
                emitter,
                raw: message
            })));
        },
        sendMessage: async (body) => {

            const { message } = await http.post(`/__e2e__/twilio/conversations/${raw.sid}/messages`, {
                author: raw.currentIdentity,
                body,
                attributes: {}
            });
            const created = internals.createMessage({ http, emitter, raw: message });
            emitter.emit('messageAdded', created);

            return created.sid;
        },
        updateLastReadMessageIndex: async (lastReadMessageIndex) => {

            await http.post(`/__e2e__/twilio/conversations/${raw.sid}/read`, { lastReadMessageIndex });

            return lastReadMessageIndex;
        }
    };

    return conversation;
};

exports.create = async (token, context) => {

    const emitter = internals.createEmitter();
    const http = internals.createHttp(context);
    const state = {
        token,
        identity: Storage.getItem(PersistenceConfig.keys.e2e.twilioIdentity) || null
    };

    const hydrateConversation = (raw) => internals.createConversation({
        http,
        emitter,
        raw: {
            ...raw,
            currentIdentity: state.identity
        }
    });

    return {
        getConversationBySid: async (sid) => {

            const { conversation } = await http.get(`/__e2e__/twilio/conversations/${sid}`);

            return hydrateConversation(conversation);
        },
        getConversationByUniqueName: async (uniqueName) => {

            const { conversations } = await http.get('/__e2e__/twilio/conversations');
            const conversation = conversations.find((candidate) => candidate.uniqueName === uniqueName);

            if (!conversation) {
                const error = new Error(`Conversation not found: ${uniqueName}`);
                error.code = 20404;
                throw error;
            }

            return hydrateConversation(conversation);
        },
        getSubscribedConversations: async () => {

            const identityQuery = state.identity ? `?identity=${encodeURIComponent(state.identity)}` : '';
            const { conversations } = await http.get(`/__e2e__/twilio/conversations${identityQuery}`);

            return internals.createPaginator(conversations.map(hydrateConversation));
        },
        on: emitter.on,
        removeAllListeners: emitter.removeAllListeners,
        removeListener: emitter.removeListener,
        shutdown: async () => {

            emitter.removeAllListeners();
        },
        updateToken: async (nextToken) => {

            state.token = nextToken;
        }
    };
};

exports.isEnabled = () => {

    if (typeof window === 'undefined') {
        return false;
    }

    return Storage.getItem(PersistenceConfig.keys.e2e.mockTwilio) === '1'
        || Storage.getItem(PersistenceConfig.keys.e2e.mockCommunication) === '1'
        || process.env.PLAYWRIGHT_E2E === 'true';
};
